/**
 * recentScans — 홈 §4 "최근 스캔한 카드" (FN-050) 로컬 기록.
 *
 * 서버가 아니라 **localStorage** 다. FN-050 은 "저장 여부와 무관"한 휘발성 이력이라
 * 회원 데이터가 아니고, 비회원 스캔(§4 비회원 5회)도 똑같이 남아야 한다. 기기를 바꾸면
 * 사라지는 것이 정책이다 — 계속 보고 싶으면 컬렉션에 담는 흐름(§6.1)으로 유도한다.
 *
 * 보관 상한 10건 (2026-08-03 사용자 확정 — homeSections 시절 주석 승계):
 * IA·기능명세서 본문의 "최근 20개"는 미갱신 상태다. 초과분은 폐기하고 복구하지 않는다.
 *
 * 저장하는 건 **cardSerialNo 목록뿐**이다. 카드 이름·시세를 저장하면 카탈로그·시세가
 * 갱신될 때 낡은 값이 화면에 남는다 — 홈이 batch API 로 그때그때 다시 읽는다.
 */

export const RECENT_SCAN_LIMIT = 10;

const KEY = "peeka.recentScans.v1";

function storage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/**
 * 구독 — 홈이 `useSyncExternalStore` 로 물린다.
 *
 * 필요한 이유: 홈은 keep-alive 메인 탭이라 스캔(오버레이 activity)을 닫고 돌아와도
 * 리마운트되지 않는다. 마운트 때 한 번 읽고 말면 방금 스캔한 카드가 §4 에 안 뜬다.
 */
const listeners = new Set<() => void>();

export function subscribeRecentScans(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** getSnapshot 안정성 — 내용이 같으면 같은 배열 참조를 돌려줘야 무한 렌더가 안 된다. */
let cache: number[] = [];

function readStorage(): number[] {
  const s = storage();
  if (!s) return [];
  try {
    const raw = s.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((v): v is number => typeof v === "number" && Number.isFinite(v))
      .slice(0, RECENT_SCAN_LIMIT);
  } catch {
    return [];
  }
}

/** 최근 스캔 카드 ID — 최신이 앞. 항상 상한 이내로 잘라 돌려준다. */
export function listRecentScans(): number[] {
  const fresh = readStorage();
  if (fresh.length === cache.length && fresh.every((v, i) => v === cache[i])) {
    return cache;
  }
  cache = fresh;
  return cache;
}

/**
 * 스캔 매칭 성공 시 기록. 같은 카드를 또 스캔하면 **맨 앞으로 끌어올린다**(중복 1건).
 * 실패해도 스캔 플로우를 깨지 않는다 — 기록은 부가 기능이다.
 */
export function recordRecentScan(cardSerialNo: number): void {
  const s = storage();
  if (!s || !Number.isFinite(cardSerialNo)) return;
  try {
    const next = [cardSerialNo, ...listRecentScans().filter((id) => id !== cardSerialNo)]
      .slice(0, RECENT_SCAN_LIMIT);
    s.setItem(KEY, JSON.stringify(next));
    listeners.forEach((listener) => listener());
  } catch {
    /* quota 등 — 기록 못 해도 무해 */
  }
}

/**
 * 기록 전체 삭제 — 계정 전환·탈퇴 시 로컬 귀속 정리(`localDataOwner`)가 부른다.
 *
 * 키를 밖에서 직접 지우지 않고 이 함수를 두는 이유: 홈 §4 가 `subscribeRecentScans`
 * 로 물려 있어, 리스너를 안 깨우면 화면에 이전 사용자의 카드가 그대로 남는다.
 */
export function clearRecentScans(): void {
  const s = storage();
  if (!s) return;
  try {
    s.removeItem(KEY);
  } catch {
    /* 지우지 못해도 흐름을 깨지 않는다 */
  }
  cache = [];
  listeners.forEach((listener) => listener());
}

export const __RECENT_SCANS_KEY__ = KEY;
